<template>
  <div class="report-container">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>正在加载报告...</p>
    </div>
    
    <!-- 错误状态 -->
    <div v-if="error" class="error">
      <p>加载报告失败: {{ error }}</p>
      <button @click="onRetry">重试</button>
    </div>
    
    <!-- 报告内容 - 外层添加命名空间容器 -->
    <div v-if="htmlContent" class="report-content" :class="styleNamespace">
      <div v-html="htmlContent"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EnergyStorageReport',
  props: {
    htmlContent: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    rawHtml: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      styleElement: null,
      // 生成唯一命名空间，用于隔离样式
      styleNamespace: `report-namespace-${Date.now()}-${Math.floor(Math.random() * 1000)}`
    }
  },
  watch: {
    rawHtml(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.extractAndApplyStyles(newVal);
      }
    }
  },
  mounted() {
    if (this.rawHtml) {
      this.extractAndApplyStyles(this.rawHtml);
    }
  },
  beforeDestroy() {
    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
    }
  },
  methods: {
    extractAndApplyStyles(html) {
      // 移除旧样式
      if (this.styleElement && this.styleElement.parentNode) {
        this.styleElement.parentNode.removeChild(this.styleElement);
      }
      
      // 解析HTML提取样式
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      let styles = '';
      const styleTags = doc.querySelectorAll('style');
      
      styleTags.forEach(tag => {
        let styleContent = tag.innerHTML;
        
        // 过滤body样式
        styleContent = styleContent.replace(/body\s*\{[^}]*\}/g, '');
        styleContent = styleContent.replace(/body\.[a-zA-Z0-9_-]+\s*\{[^}]*\}/g, '');
        
        // 给所有选择器添加命名空间前缀，模拟scoped效果
        // 处理常见选择器情况（元素、类、ID选择器等）
        styleContent = this.addNamespaceToStyles(styleContent, this.styleNamespace);
        
        styles += styleContent + '\n';
      });
      
      // 创建新样式元素
      this.styleElement = document.createElement('style');
      this.styleElement.type = 'text/css';
      this.styleElement.innerHTML = styles;
      document.head.appendChild(this.styleElement);
    },
    
    // 给样式选择器添加命名空间前缀
    addNamespaceToStyles(css, namespace) {
      // 处理多行注释
      css = css.replace(/\/\*[\s\S]*?\*\//g, '');
      
      // 分割CSS规则（简单处理，复杂情况可能需要更完善的解析）
      const rules = css.split('}');
      
      // 给每个选择器添加命名空间
      const scopedRules = rules.map(rule => {
        if (!rule.trim()) return '';
        
        // 分割选择器和样式内容
        const [selectors, styles] = rule.split('{');
        if (!selectors || !styles) return rule;
        
        // 给每个选择器添加命名空间前缀
        const scopedSelectors = selectors.split(',').map(selector => {
          selector = selector.trim();
          if (!selector) return '';
          
          // 处理不同类型的选择器
          if (selector === '*') {
            return `.${namespace} *`;
          } else if (selector.startsWith('.')) {
            return `.${namespace} ${selector}`;
          } else if (selector.startsWith('#')) {
            return `.${namespace} ${selector}`;
          } else if (selector.includes(':')) {
            // 处理伪类选择器
            const [base, pseudo] = selector.split(':');
            return `.${namespace} ${base}:${pseudo}`;
          } else {
            // 元素选择器
            return `.${namespace} ${selector}`;
          }
        }).filter(Boolean).join(', ');
        
        return scopedSelectors ? `${scopedSelectors} { ${styles} }` : '';
      });
      
      return scopedRules.filter(Boolean).join('}') + '}';
    },
    
    onRetry() {
      this.$emit('retry');
    }
  }
}
</script>

<style scoped>
.report-container {
  min-height: 100vh;
  padding: 20px;
  background-color: #f4f4f4;
}

.report-content {
  width: 100%;
}

/* 加载状态样式 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(52, 152, 219, 0.2);
  border-left-color: #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误状态样式 */
.error {
  text-align: center;
  padding: 20px;
  color: #e74c3c;
}

.error button {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.error button:hover {
  background-color: #2980b9;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .report-container {
    padding: 10px;
  }
}
</style>
